const API_URL = "https://portal-spf-api.uniithra.com.sa/api/v1/home/authenticate"

const TOKEN_KEY = "crm_auth_token"
const EXPIRY_KEY = "crm_token_expiry"
const REFRESH_INTERVAL = 60 * 60 * 1000
const EXPIRY_BUFFER = 5 * 60 * 1000
const API_TIMEOUT = 30000
const MAX_RETRIES = 3;

let refreshTimer = null

const fetchWithTimeout = async (url, options, timeout) => {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)
  
  try {
    const response = await fetch(url, { ...options, signal: controller.signal })
    clearTimeout(timeoutId)
    return response
  } catch (error) {
    clearTimeout(timeoutId)
    if (error.name === 'AbortError') throw new Error('Request timeout')
    throw error
  }
}

const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))

const fetchWithRetry = async (url, options, timeout, retries = MAX_RETRIES) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      const response = await fetchWithTimeout(url, options, timeout);

      if ((response.status === 504 || response.status === 408) && attempt < retries) {
        await delay(1000 * attempt);
        continue;
      }

      return response;
    } catch (err) {
      if (attempt < retries) {
        await delay(1000 * attempt);
      } else {
        throw err;
      }
    }
  }
};

const storage = {
  getToken: () => typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null,
  
  getExpiry: () => {
    if (typeof window === "undefined") return null
    const expiry = localStorage.getItem(EXPIRY_KEY)
    return expiry ? parseInt(expiry, 10) : null
  },

  setToken: (token) => {
    if (typeof window === "undefined") return
    localStorage.setItem(TOKEN_KEY, token)
    localStorage.setItem(EXPIRY_KEY, (Date.now() + REFRESH_INTERVAL).toString())
  },

  clear: () => {
    if (typeof window === "undefined") return
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(EXPIRY_KEY)
  },

  isExpired: () => {
    const expiry = storage.getExpiry()
    return !expiry || Date.now() >= expiry - EXPIRY_BUFFER
  }
}

async function authenticate(username, password, language = "en") {
  try {
    const response = await fetchWithRetry(API_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "accept-language": language
      },
      body: JSON.stringify({ username, password })
    }, API_TIMEOUT)

    const data = await response.json()

    if (data.succeeded && data.data?.accessToken) {
      storage.setToken(data.data.accessToken)
      return { success: true, token: data.data.accessToken }
    }

    return { 
      success: false, 
      error: data.message?.body || data.message?.title || "Authentication failed" 
    }
  } catch (err) {
    return { success: false, error: err.message || "Network error" }
  }
}

async function getValidToken(username, password, language = "en") {
  const token = storage.getToken()
  if (token && !storage.isExpired()) return { success: true, token }
  return authenticate(username, password, language)
}

function startAutoRefresh(username, password, language = "en") {
  if (typeof window === "undefined") return
  stopAutoRefresh()
  refreshTimer = setInterval(() => authenticate(username, password, language), REFRESH_INTERVAL)
}

function stopAutoRefresh() {
  if (refreshTimer) {
    clearInterval(refreshTimer)
    refreshTimer = null
  }
}

export async function initializeAuth(username, password, language = "en") {
  if (typeof window === "undefined") return { success: false }
  const result = await getValidToken(username, password, language)
  if (result.success) startAutoRefresh(username, password, language)
  return result
}

export { getValidToken }
